import { FormEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ErrorAlert from '@/shared/components/ErrorAlert';
import FormField from '@/shared/components/FormField';
import FormInput from '@/shared/components/FormInput';
import Button from '@/shared/components/Button';

interface Props {
  email: string;
  error: string | null;
  isLoading: boolean;
  isSuccess: boolean;
  onEmailChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  onBackToSignIn: () => void;
}

export default function PasswordResetRequestForm({
  email,
  error,
  isLoading,
  isSuccess,
  onEmailChange,
  onSubmit,
  onBackToSignIn,
}: Props) {
  const intl = useIntl();

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
          <FormattedMessage id="auth.passwordReset.greeting" />
        </p>
        <h2 className="mt-2 text-2xl font-semibold text-slate-900">
          <FormattedMessage id="auth.passwordReset.heading" />
        </h2>
      </div>

      {error && <ErrorAlert message={error} />}

      {isSuccess ? (
        <div className="rounded-xl bg-green-50 p-4 text-sm text-green-700">
          <FormattedMessage id="auth.passwordReset.successMessage" />
        </div>
      ) : (
        <form className="space-y-5" onSubmit={onSubmit}>
          <FormField id="email" labelId="auth.passwordReset.emailLabel">
            <FormInput
              id="email"
              type="email"
              autoComplete="email"
              required
              placeholder={intl.formatMessage({ id: 'auth.passwordReset.emailPlaceholder' })}
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
            />
          </FormField>

          <Button type="submit" disabled={isLoading} fullWidth>
            {isLoading ? (
              <FormattedMessage id="auth.passwordReset.submitting" />
            ) : (
              <FormattedMessage id="auth.passwordReset.submit" />
            )}
          </Button>
        </form>
      )}

      <div className="text-center">
        <button
          type="button"
          onClick={onBackToSignIn}
          className="text-sm font-semibold text-brand-700 hover:text-brand-600"
        >
          <FormattedMessage id="auth.passwordReset.backToSignIn" />
        </button>
      </div>
    </div>
  );
}
